import React from 'react';
import {
  Activity,
  TrendingDown,
  TrendingUp,
  Shield,
  Zap,
  AlertTriangle,
  CheckCircle,
  Clock,
  BarChart2,
  Target,
  XCircle,
} from 'lucide-react';
import {
  useBotAPI,
  type BotAnalysis,
  type BacktestStats,
  type SetupTrackingResponse,
} from '../lib/useBotAPI';
import { useJournalStats } from '../lib/useJournalStats';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const StatusDot: React.FC<{ level: string }> = ({ level }) => {
  const color =
    level === 'GREEN'
      ? 'bg-green-500'
      : level === 'YELLOW'
      ? 'bg-yellow-500'
      : 'bg-red-500';
  return (
    <span className={`inline-block h-3 w-3 rounded-full ${color} mr-2 animate-pulse`} />
  );
};

const Metric: React.FC<{
  label: string;
  value: string | number;
  icon: React.ReactNode;
  sub?: string;
}> = ({ label, value, icon, sub }) => (
  <div className="flex items-start space-x-3">
    <div className="p-2 bg-white/5 rounded-lg shrink-0">{icon}</div>
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-lg font-bold text-white">{value}</p>
      {sub && <p className="text-xs text-gray-500">{sub}</p>}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main Panel
// ---------------------------------------------------------------------------

interface BotPanelProps {
  isAdmin?: boolean;
}

const BotPanel: React.FC<BotPanelProps> = ({ isAdmin = false }) => {
  const {
    data: analysis,
    loading: loadingAnalysis,
    error: errorAnalysis,
  } = useBotAPI<BotAnalysis>('/api/analisis');

  const {
    data: backtest,
    loading: loadingBacktest,
  } = useBotAPI<{ backtest: BacktestStats }>('/api/setups');

  const {
    data: tracking,
  } = useBotAPI<SetupTrackingResponse>('/api/setups-tracking');

  const { stats: journalStats, account } = useJournalStats();

  const loading = loadingAnalysis || loadingBacktest;

  if (loading) {
    return (
      <div className="card p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Activity className="h-5 w-5 text-primary animate-pulse" />
          <h2 className="text-xl font-bold text-white">Mentor en Vivo</h2>
        </div>
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  if (errorAnalysis) {
    return (
      <div className="card p-6">
        <div className="flex items-center space-x-3 mb-4">
          <AlertTriangle className="h-5 w-5 text-yellow-500" />
          <h2 className="text-xl font-bold text-white">Mentor</h2>
        </div>
        <p className="text-gray-400 text-sm">
          Mentor offline o sin datos recientes. El mentor analiza NQ durante sesiones de mercado (15:30-20:50 hora española · Power Hour excluida).
        </p>
      </div>
    );
  }

  const bt = backtest?.backtest;
  const biasEmoji = analysis?.daily_bias === 'bearish' ? '📉' : analysis?.daily_bias === 'bullish' ? '📈' : '➡️';
  const biasText = analysis?.daily_bias === 'bearish' ? 'BAJISTA' : analysis?.daily_bias === 'bullish' ? 'ALCISTA' : 'NEUTRAL';

  const setup = analysis?.setup;
  const hasSetup = setup && !setup.wait_for && setup.entry_zone_low > 0;

  // Price change
  const price = (analysis as any)?.current_price || 0;
  const prevClose = (analysis as any)?.prev_close || 0;
  const priceChange = price && prevClose ? price - prevClose : 0;
  const changeSign = priceChange >= 0 ? '+' : '';
  const changeColor = priceChange >= 0 ? 'text-green-400' : 'text-red-400';

  // Setups tracking — count live setups this month
  const liveSetups = tracking?.setups?.filter(s => s.setup_type === 'live') || [];
  const liveSetupsCount = liveSetups.length;

  return (
    <div className="space-y-6">
      <div className="card p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Activity className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-bold text-white">Mentor en Vivo</h2>
          </div>
          {isAdmin && (
            <div className="flex items-center">
              <StatusDot level={account.riskLevel} />
              <span className="text-sm text-gray-400">
                {account.riskLevel === 'GREEN' ? 'OK' : account.riskLevel === 'YELLOW' ? 'Precaucion' : 'Peligro'}
              </span>
            </div>
          )}
        </div>

        {/* 1. Precio actual de NQ */}
        {price > 0 && (
          <div className="bg-gradient-to-r from-white/5 to-transparent rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 mb-1">Nasdaq (NQ)</p>
                <p className="text-2xl font-extrabold text-white font-mono">
                  {price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </p>
              </div>
              {prevClose > 0 && (
                <div className={`text-right ${changeColor}`}>
                  <p className="text-lg font-bold font-mono">
                    {changeSign}{priceChange.toFixed(0)} pts
                  </p>
                  <p className="text-xs opacity-75">
                    {changeSign}{((priceChange / prevClose) * 100).toFixed(2)}%
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bias + Zone */}
        {analysis && (
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-xs text-gray-400 mb-1">Bias del dia</p>
              <p className="text-lg font-bold text-white">
                {biasEmoji} {biasText}
              </p>
              <p className="text-xs text-gray-500">
                Confianza: {((analysis.bias_confidence || 0) * 100).toFixed(0)}%
              </p>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-xs text-gray-400 mb-1">Zona actual</p>
              <p className="text-lg font-bold text-white capitalize">
                {analysis.current_zone === 'premium' ? '🔴 Premium' :
                 analysis.current_zone === 'discount' ? '🟢 Descuento' : '🟡 Equilibrio'}
              </p>
              <p className="text-xs text-gray-500">
                {analysis.confluence_count || 0} señales ICT activas
              </p>
            </div>
          </div>
        )}

        {/* Active Setup */}
        {hasSetup && (
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <Target className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary uppercase">
                Setup Activo — {setup.direction === 'bearish' ? 'SHORT' : 'LONG'}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-gray-400">Entrada</p>
                <p className="text-white font-mono">
                  {setup.entry_zone_low.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <p className="text-gray-400">Stop Loss</p>
                <p className="text-red-400 font-mono">
                  {setup.stop_loss.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <p className="text-gray-400">Target</p>
                <p className="text-green-400 font-mono">
                  {setup.take_profit.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              R:R 1:{setup.rr_ratio.toFixed(1)} | {setup.confluences?.length} confluencias de entrada: {setup.confluences?.join(', ')}
            </p>
          </div>
        )}

        {setup?.wait_for && (
          <div className="bg-yellow-900/20 border border-yellow-800/30 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4 text-yellow-500" />
              <span className="text-sm text-yellow-200">{setup.wait_for}</span>
            </div>
          </div>
        )}

        {/* 3. Risk (solo admin) + Backtest (todos) */}
        <div className={`grid ${isAdmin ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-2'} gap-4`}>
          {isAdmin && (
            <>
              <Metric
                label="Balance"
                value={`$${account.currentBalance.toLocaleString()}`}
                icon={<Shield className="h-4 w-4 text-blue-400" />}
                sub={`MLL: $${account.mllLevel.toLocaleString()}`}
              />
              <Metric
                label="Margen MLL"
                value={`$${account.distanceToMll.toLocaleString()}`}
                icon={<AlertTriangle className="h-4 w-4 text-yellow-400" />}
                sub={`Max: ${account.maxMinis} minis`}
              />
            </>
          )}
          {journalStats && journalStats.totalTrades > 0 ? (
            <>
              <Metric
                label="Ratio de eficiencia estructural"
                value={journalStats.profitFactor === Infinity ? '∞' : journalStats.profitFactor.toFixed(2)}
                icon={<BarChart2 className="h-4 w-4 text-green-400" />}
                sub={`${journalStats.totalTrades} escenario${journalStats.totalTrades !== 1 ? 's' : ''}`}
              />
              <Metric
                label="Tasa de escenarios completados"
                value={`${journalStats.winRate}%`}
                icon={<CheckCircle className="h-4 w-4 text-emerald-400" />}
              />
            </>
          ) : bt ? (
            <>
              <Metric
                label="Ratio de eficiencia estructural"
                value={bt.profit_factor.toFixed(2)}
                icon={<BarChart2 className="h-4 w-4 text-green-400" />}
                sub={`${bt.total_trades} escenarios (backtest)`}
              />
              <Metric
                label="Tasa de escenarios completados"
                value={`${bt.win_rate}%`}
                icon={<CheckCircle className="h-4 w-4 text-emerald-400" />}
              />
            </>
          ) : null}
        </div>

        {/* Disclaimer métricas */}
        {(journalStats?.totalTrades > 0 || bt) && (
          <p className="text-xs text-gray-600 mt-2">
            Comportamiento historico del modelo bajo condiciones especificas. No representa resultados futuros.
          </p>
        )}

        {/* Kill Zone */}
        {analysis?.kill_zone_active && (
          <div className="flex items-center space-x-2 text-sm">
            <Zap className="h-4 w-4 text-primary" />
            <span className="text-gray-300">
              Kill Zone activa:{' '}
              <span className="text-white font-semibold">
                {analysis.kill_zone_active === 'ny_am'
                  ? '🇺🇸 New York AM'
                  : analysis.kill_zone_active === 'london'
                  ? '🇬🇧 London'
                  : '⚡ Power Hour'}
              </span>
            </span>
          </div>
        )}

        {/* Footer */}
        {analysis?._generated && (
          <p className="text-xs text-gray-600 text-right">
            Ultimo analisis: {new Date(analysis._generated).toLocaleString('es-ES')}
          </p>
        )}
      </div>

      {/* 2. Setups de hoy */}
      {liveSetups.length > 0 && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Target className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-bold text-white">Setups de Hoy</h3>
            </div>
            {/* P&L total removido — regulatorio MiFID II / CNMV */}
          </div>
          <div className="space-y-2">
            {liveSetups.map((s) => {
              const dir = s.direction === 'bearish' ? 'Short' : 'Long';
              const entryMid = ((s.entry_min + s.entry_max) / 2);
              let icon, statusText, statusColor;

              // Detectar zona stale (>30pts del precio actual)
              const distance = price > 0 ? Math.abs(entryMid - price) : 0;
              const isStale = s.status === 'pending' && distance > 30;

              if (s.status === 'pending' && isStale) {
                icon = <Clock className="h-4 w-4 text-gray-500" />;
                statusText = `zona lejana — ${distance.toFixed(0)} pts`;
                statusColor = 'text-gray-500';
              } else if (s.status === 'pending') {
                icon = <Clock className="h-4 w-4 text-yellow-400" />;
                statusText = 'pendiente';
                statusColor = 'text-yellow-400';
              } else if (s.status === 'triggered') {
                icon = <Activity className="h-4 w-4 text-blue-400 animate-pulse" />;
                statusText = 'en trade';
                statusColor = 'text-blue-400';
              } else if (s.close_reason === 'timeout_in_trade') {
                const pnl = s.pnl_usd || 0;
                if (pnl >= 0) {
                  icon = <CheckCircle className="h-4 w-4 text-green-400" />;
                  statusText = 'Completado';
                  statusColor = 'text-green-400';
                } else {
                  icon = <XCircle className="h-4 w-4 text-red-400" />;
                  statusText = 'Invalidado';
                  statusColor = 'text-red-400';
                }
              } else if (s.status === 'won' || s.outcome === 'won') {
                icon = <CheckCircle className="h-4 w-4 text-green-400" />;
                statusText = 'Completado';
                statusColor = 'text-green-400';
              } else if (s.status === 'lost' || s.outcome === 'lost' || s.outcome === 'ambiguous_intrabar') {
                icon = <XCircle className="h-4 w-4 text-red-400" />;
                statusText = 'Invalidado';
                statusColor = 'text-red-400';
              } else if (s.close_reason === 'timeout_no_trigger' || s.status === 'expired') {
                icon = <Clock className="h-4 w-4 text-gray-500" />;
                statusText = 'expirado sin activar';
                statusColor = 'text-gray-500';
              } else {
                icon = <Clock className="h-4 w-4 text-gray-500" />;
                statusText = s.status;
                statusColor = 'text-gray-500';
              }

              return (
                <div key={s.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div className="flex items-center space-x-3">
                    {icon}
                    <span className="text-white text-sm">
                      {dir} — Zona técnica {entryMid.toLocaleString('en-US', { minimumFractionDigits: 0 })}
                    </span>
                  </div>
                  <span className={`text-sm font-medium ${statusColor}`}>
                    {statusText}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
};

export default BotPanel;
